import { Checkbox } from 'primereact/checkbox'
import { useTranslation } from 'react-i18next'
import FilterBar from '../../../components/ui/FilterBar'
import MobileSheetDropdown from '../../../components/ui/MobileSheetDropdown'
import MultiSelect from '../../../components/ui/MultiSelect'
import { useActionStatuses } from '../../actions/hooks/useActionStatuses'
import {
  useMeetingGroupOptions,
  useResponsibleOptions,
  useTaskCreators,
} from '../hooks/useMeetingManagement'
import { DELAY_SPLIT_STATUS_IDS, FILTERABLE_STATUS_IDS, statusKey } from '../types'

export interface MeetingManagementFiltersValue {
  /** Durum süzgecinin seçili değerleri — bkz. {@link statusKey}. */
  statusKeys: string[]
  creatorUserId: number | null
  mainResponsibleEmployeeId: string | null
  supportManagerEmployeeId: string | null
  /** Çoklu seçim; boş → grup süzgeci yok. */
  meetingGroupIds: number[]
  /** "Doğrudan Görevler" — toplantısız görevler listeye girsin mi (grup seçiliyken de). */
  includeDirectTasks: boolean
  includeSubTasks: boolean
}

interface Props {
  value: MeetingManagementFiltersValue
  onChange: (next: MeetingManagementFiltersValue) => void
  onClear: () => void
  hasFilter: boolean
}

/**
 * Toplantı Yönetimi süzgeç çubuğu (105): **Toplantı Grubu · Görevi Oluşturan · Ana Sorumlu · Süreç
 * Destek Yöneticisi · Durum** + "Doğrudan Görevler" + "Alt görevleri de getir".
 *
 * <p><b>Toplantı Grubu çoklu seçimdir</b> ve yalnız toplantıya bağlı görevleri daraltır. Doğrudan
 * (toplantısız) görevin grubu yoktur; onu yanındaki kutu yönetir — işaretliyse seçili grupların
 * görevlerine eklenir, kaldırılırsa liste yalnız toplantılı görevlerden oluşur.
 *
 * <p>Arama kutusu **yoktur** (analizde geçmiyor); {@code FilterBar} onu {@code onSearch}
 * verilmediğinde çizmez.
 *
 * <p><b>Dört seçenek listesi kendi uçlarından beslenir</b> ve ekranın diğer süzgeçlerine bağlı
 * değildir: kullanıcı bir süzgeci değiştirdiğinde ötekinin seçenekleri kaybolmaz (FR-011).
 *
 * <p><b>Kişi süzgeçleri rol bazında AYRIDIR.</b> Birleşik bir liste verilseydi kullanıcı "Ana
 * Sorumlu" kutusundan yalnızca Süreç Destek Yöneticisi olan birini seçebilir ve sebebi ekranda
 * görünmeyen boş bir sonuç alırdı.
 */
export default function MeetingManagementFilters({
  value,
  onChange,
  onClear,
  hasFilter,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const meetingGroups = useMeetingGroupOptions()
  const creators = useTaskCreators()
  const mainResponsibles = useResponsibleOptions('main')
  const supportManagers = useResponsibleOptions('support')
  // Durum ADLARI sunucudan (dile-duyarlı); istemci sabit eşleme kurmaz. 339 Arşivlendi bu ekranda hiç
  // listelenmez (arşiv ayrı bir ekran); 332 ise 22.09.2026'da kataloğdan kalktı.
  const statuses = useActionStatuses()

  // Aksiyon Bekleniyor ve Onay Bekleniyor gecikmeye göre ikiye bölünür ("… - Geciken" /
  // "… - Gecikmeyen", 30.09.2026 kullanıcı isteği); diğer durumlar tek seçenektir.
  const delayed = t('meetingManagement.filter.delayed')
  const onTime = t('meetingManagement.filter.onTime')
  const statusOptions = (statuses.data ?? [])
    .filter((s) => FILTERABLE_STATUS_IDS.includes(s.id))
    .flatMap((s) =>
      DELAY_SPLIT_STATUS_IDS.includes(s.id)
        ? [
            { label: `${s.name} - ${delayed}`, value: statusKey(s.id, 'delayed') },
            { label: `${s.name} - ${onTime}`, value: statusKey(s.id, 'onTime') },
          ]
        : [{ label: s.name, value: statusKey(s.id) }],
    )

  const meetingGroupOptions = (meetingGroups.data ?? []).map((g) => ({
    label: g.name,
    value: g.id,
  }))

  const creatorOptions = (creators.data ?? []).map((c) => ({
    label: c.displayName,
    value: c.userId,
  }))

  const personOptions = (people: { employeeId: string; fullName: string }[] | undefined) =>
    (people ?? []).map((p) => ({ label: p.fullName, value: p.employeeId }))

  const set = <K extends keyof MeetingManagementFiltersValue>(
    key: K,
    next: MeetingManagementFiltersValue[K],
  ) => onChange({ ...value, [key]: next })

  return (
    <FilterBar
      className="mm-filterbar"
      showClear={hasFilter}
      clearLabel={t('meetingManagement.filter.clear')}
      onClear={onClear}
    >
      {/*
        Durum süzgeciyle aynı kalıp: tek seçimde grubun adı, birden fazlasında "{n} grup seçili"
        yazar; çip dizip kutuyu uzatmaz. `showClear`: tekli hâlindeki tek tıkla temizleme korunur —
        aksi hâlde grupları sıfırlamanın yolu ya tek tek işaret kaldırmak ya da TÜM süzgeçleri
        sıfırlayan "Filtreleri Temizle" olurdu.
      */}
      <MultiSelect
        className="mm-filter-group"
        value={value.meetingGroupIds}
        options={meetingGroupOptions}
        onChange={(e) => set('meetingGroupIds', (e.value as number[] | null) ?? [])}
        placeholder={t('meetingManagement.filter.meetingGroup')}
        aria-label={t('meetingManagement.filter.meetingGroup')}
        display="comma"
        maxSelectedLabels={1}
        selectedItemsLabel={t('meetingManagement.filter.meetingGroupSelected')}
        showClear
        filter
      />
      <MobileSheetDropdown
        value={value.creatorUserId}
        options={creatorOptions}
        onChange={(e) => set('creatorUserId', (e.value as number | null) ?? null)}
        placeholder={t('meetingManagement.filter.creator')}
        aria-label={t('meetingManagement.filter.creator')}
        showClear
        filter
      />
      <MobileSheetDropdown
        value={value.mainResponsibleEmployeeId}
        options={personOptions(mainResponsibles.data)}
        onChange={(e) => set('mainResponsibleEmployeeId', (e.value as string | null) ?? null)}
        placeholder={t('meetingManagement.filter.mainResponsible')}
        aria-label={t('meetingManagement.filter.mainResponsible')}
        showClear
        filter
      />
      <MobileSheetDropdown
        value={value.supportManagerEmployeeId}
        options={personOptions(supportManagers.data)}
        onChange={(e) => set('supportManagerEmployeeId', (e.value as string | null) ?? null)}
        placeholder={t('meetingManagement.filter.supportManager')}
        aria-label={t('meetingManagement.filter.supportManager')}
        showClear
        filter
      />
      {/*
        `maxSelectedLabels={1}`: birden fazla seçimde kutu "{n} seçili" der, çipler yan yana
        dizilip kutuyu satır boyuna uzatmaz. Paylaşılan bileşen varsayılan olarak çip gösterir ve
        genişliği %100'dür — burada `mm-filter-status` ile daraltılır (kullanıcı bildirimi
        2026-09-16: "durum inputunun genişliği inanılmaz büyük").
      */}
      <MultiSelect
        className="mm-filter-status"
        value={value.statusKeys}
        options={statusOptions}
        onChange={(e) => set('statusKeys', (e.value as string[]) ?? [])}
        placeholder={t('meetingManagement.filter.status')}
        aria-label={t('meetingManagement.filter.status')}
        display="comma"
        maxSelectedLabels={1}
        selectedItemsLabel={t('meetingManagement.filter.statusSelected')}
      />
      {/*
        Etiket `htmlFor` ile kutuya bağlanır: tıklanabilir alan büyür ve ekran okuyucu kutunun ne
        olduğunu söyler. Kutu ile etiket AYRI kardeşlerdir — etiketi kutunun içine sarmak iç içe
        interaktif kontrol üretirdi (bu ekran ailesinde 2026-09-15'te iki kez bulgu çıktı).
      */}
      <span className="mm-filter-check">
        <Checkbox
          inputId="mm-include-direct"
          checked={value.includeDirectTasks}
          onChange={(e) => set('includeDirectTasks', !!e.checked)}
        />
        <label htmlFor="mm-include-direct">{t('meetingManagement.filter.includeDirectTasks')}</label>
      </span>
      <span className="mm-filter-check">
        <Checkbox
          inputId="mm-include-subtasks"
          checked={value.includeSubTasks}
          onChange={(e) => set('includeSubTasks', !!e.checked)}
        />
        <label htmlFor="mm-include-subtasks">
          {t('meetingManagement.filter.includeSubTasks')}
        </label>
      </span>
    </FilterBar>
  )
}
